/**
 * Pillar Service - Firestore operations for marketing pillars management
 *
 * Marketing pillars are content themes that define recurring categories for a brand.
 * Each pillar maps to customer awareness stages and funnel stages.
 */

import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';

const PILLARS_COLLECTION = 'pillars';

/**
 * Default pillar structure with awareness stage mappings
 */
export const DEFAULT_PILLAR = {
    name: '',
    description: '',
    color: '#6366F1', // Default indigo
    icon: 'Lightbulb',

    contentGuidelines: {
        toneOfVoice: [],
        exampleTopics: [],
        keyMessages: []
    },

    // Customer awareness stage mappings (primary)
    awarenessMapping: {
        unaware: { recommended: false, weight: 0 },
        problemAware: { recommended: false, weight: 0 },
        solutionAware: { recommended: false, weight: 0 },
        productAware: { recommended: false, weight: 0 },
        mostAware: { recommended: false, weight: 0 }
    },

    // Funnel mapping (derived from awareness stages)
    funnelMapping: {
        TOFU: { recommended: false, weight: 0 },
        MOFU: { recommended: false, weight: 0 },
        BOFU: { recommended: false, weight: 0 }
    },

    // Performance metrics
    performance: {
        totalCreatives: 0,
        pipelineMetrics: {
            avgReach: 0,
            avgEngagementRate: 0,
            audienceGrowth: 0
        },
        revenueMetrics: {
            avgConversions: 0,
            avgROAS: 0,
            avgCPA: 0
        },
        lastCalculated: null
    },

    // Frequency settings
    frequency: {
        currentWeight: 0,
        recommendedWeight: 0,
        postsPerWeek: 0
    },

    isArchived: false
};

/**
 * Awareness stage labels for UI
 */
export const AWARENESS_STAGES = {
    unaware: {
        label: 'Unaware',
        description: "Doesn't know they have a problem",
        contentFocus: 'Entertainment, lifestyle, trends'
    },
    problemAware: {
        label: 'Problem Aware',
        description: 'Knows the problem, not solutions',
        contentFocus: 'Educational content, pain point content'
    },
    solutionAware: {
        label: 'Solution Aware',
        description: 'Knows solutions exist, not your brand',
        contentFocus: 'Comparison, how-to, thought leadership'
    },
    productAware: {
        label: 'Product Aware',
        description: 'Knows your product, evaluating',
        contentFocus: 'Social proof, features, testimonials'
    },
    mostAware: {
        label: 'Most Aware',
        description: 'Ready to buy, needs final push',
        contentFocus: 'Offers, urgency, easy checkout'
    }
};

/**
 * Funnel stage labels for UI
 */
export const FUNNEL_STAGES = {
    TOFU: {
        label: 'Top of Funnel',
        description: 'Awareness stage',
        awarenessStages: ['unaware', 'problemAware']
    },
    MOFU: {
        label: 'Middle of Funnel',
        description: 'Consideration stage',
        awarenessStages: ['solutionAware', 'productAware']
    },
    BOFU: {
        label: 'Bottom of Funnel',
        description: 'Conversion stage',
        awarenessStages: ['mostAware']
    }
};

/**
 * Preset pillar templates for quick setup
 */
export const PILLAR_PRESETS = [
    {
        name: 'Education',
        description: 'Educational content that teaches and informs your audience about topics related to your product/service',
        color: '#10B981',
        icon: 'GraduationCap',
        awarenessMapping: {
            unaware: { recommended: true, weight: 30 },
            problemAware: { recommended: true, weight: 40 },
            solutionAware: { recommended: true, weight: 20 },
            productAware: { recommended: false, weight: 10 },
            mostAware: { recommended: false, weight: 0 }
        },
        contentGuidelines: {
            toneOfVoice: ['informative', 'helpful', 'authoritative'],
            exampleTopics: ['How-to guides', 'Tips & tricks', 'Industry insights', 'Common mistakes to avoid'],
            keyMessages: []
        }
    },
    {
        name: 'Entertainment',
        description: 'Fun, engaging content that builds brand affinity and reaches new audiences',
        color: '#F59E0B',
        icon: 'Sparkles',
        awarenessMapping: {
            unaware: { recommended: true, weight: 50 },
            problemAware: { recommended: true, weight: 30 },
            solutionAware: { recommended: false, weight: 15 },
            productAware: { recommended: false, weight: 5 },
            mostAware: { recommended: false, weight: 0 }
        },
        contentGuidelines: {
            toneOfVoice: ['playful', 'relatable', 'trendy'],
            exampleTopics: ['Memes', 'Trends', 'Behind-the-scenes fun', 'User challenges'],
            keyMessages: []
        }
    },
    {
        name: 'Social Proof',
        description: 'Customer testimonials, reviews, case studies, and success stories that build trust',
        color: '#8B5CF6',
        icon: 'Users',
        awarenessMapping: {
            unaware: { recommended: false, weight: 5 },
            problemAware: { recommended: false, weight: 10 },
            solutionAware: { recommended: true, weight: 25 },
            productAware: { recommended: true, weight: 35 },
            mostAware: { recommended: true, weight: 25 }
        },
        contentGuidelines: {
            toneOfVoice: ['authentic', 'trustworthy', 'relatable'],
            exampleTopics: ['Customer testimonials', 'Case studies', 'Before/after', 'Reviews & ratings'],
            keyMessages: []
        }
    },
    {
        name: 'Product Features',
        description: 'Content highlighting product benefits, features, and differentiators',
        color: '#3B82F6',
        icon: 'Package',
        awarenessMapping: {
            unaware: { recommended: false, weight: 0 },
            problemAware: { recommended: false, weight: 10 },
            solutionAware: { recommended: true, weight: 30 },
            productAware: { recommended: true, weight: 40 },
            mostAware: { recommended: true, weight: 20 }
        },
        contentGuidelines: {
            toneOfVoice: ['clear', 'benefit-focused', 'confident'],
            exampleTopics: ['Feature demos', 'Comparison content', 'Use cases', 'New releases'],
            keyMessages: []
        }
    },
    {
        name: 'Behind the Scenes',
        description: 'Authentic content showing the people, process, and values behind your brand',
        color: '#EC4899',
        icon: 'Camera',
        awarenessMapping: {
            unaware: { recommended: true, weight: 40 },
            problemAware: { recommended: true, weight: 30 },
            solutionAware: { recommended: false, weight: 20 },
            productAware: { recommended: false, weight: 10 },
            mostAware: { recommended: false, weight: 0 }
        },
        contentGuidelines: {
            toneOfVoice: ['authentic', 'personal', 'transparent'],
            exampleTopics: ['Team intros', 'Day in the life', 'How its made', 'Company values'],
            keyMessages: []
        }
    },
    {
        name: 'Offers & Promotions',
        description: 'Sales, discounts, limited-time offers, and conversion-focused content',
        color: '#EF4444',
        icon: 'Tag',
        awarenessMapping: {
            unaware: { recommended: false, weight: 0 },
            problemAware: { recommended: false, weight: 5 },
            solutionAware: { recommended: false, weight: 10 },
            productAware: { recommended: true, weight: 30 },
            mostAware: { recommended: true, weight: 55 }
        },
        contentGuidelines: {
            toneOfVoice: ['urgent', 'exciting', 'clear'],
            exampleTopics: ['Flash sales', 'Limited offers', 'Bundle deals', 'Seasonal promotions'],
            keyMessages: []
        }
    }
];

/**
 * Create a new pillar
 */
export const createPillar = async (pillarData, userId, brandId) => {
    try {
        const { id, ...dataToSave } = pillarData;

        // Merge with defaults
        const pillar = {
            ...DEFAULT_PILLAR,
            ...dataToSave,
            userId,
            brandId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        // Derive funnel mapping from awareness mapping
        pillar.funnelMapping = deriveFunnelMapping(pillar.awarenessMapping);

        const docRef = await addDoc(collection(db, PILLARS_COLLECTION), pillar);
        return { id: docRef.id, ...pillar };
    } catch (error) {
        console.error('Error creating pillar:', error);
        throw error;
    }
};

/**
 * Get a pillar by ID
 */
export const getPillar = async (pillarId) => {
    try {
        const docRef = doc(db, PILLARS_COLLECTION, pillarId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    } catch (error) {
        console.error('Error getting pillar:', error);
        throw error;
    }
};

/**
 * Get all pillars for a brand
 */
export const getPillarsByBrand = async (brandId, includeArchived = false) => {
    try {
        let q;
        if (includeArchived) {
            q = query(
                collection(db, PILLARS_COLLECTION),
                where('brandId', '==', brandId),
                orderBy('createdAt', 'asc')
            );
        } else {
            q = query(
                collection(db, PILLARS_COLLECTION),
                where('brandId', '==', brandId),
                where('isArchived', '==', false),
                orderBy('createdAt', 'asc')
            );
        }
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting pillars by brand:', error);
        throw error;
    }
};

/**
 * Update a pillar
 */
export const updatePillar = async (pillarId, updates) => {
    try {
        const { id, ...dataToUpdate } = updates;

        // If awareness mapping is updated, derive new funnel mapping
        if (dataToUpdate.awarenessMapping) {
            dataToUpdate.funnelMapping = deriveFunnelMapping(dataToUpdate.awarenessMapping);
        }

        const docRef = doc(db, PILLARS_COLLECTION, pillarId);
        await updateDoc(docRef, {
            ...dataToUpdate,
            updatedAt: serverTimestamp()
        });
        return { id: pillarId, ...dataToUpdate };
    } catch (error) {
        console.error('Error updating pillar:', error);
        throw error;
    }
};

/**
 * Save pillar (create or update)
 */
export const savePillar = async (pillarData, userId, brandId) => {
    if (pillarData.id) {
        return updatePillar(pillarData.id, pillarData);
    }
    return createPillar(pillarData, userId, brandId);
};

/**
 * Delete a pillar permanently
 */
export const deletePillar = async (pillarId) => {
    try {
        await deleteDoc(doc(db, PILLARS_COLLECTION, pillarId));
        return true;
    } catch (error) {
        console.error('Error deleting pillar:', error);
        throw error;
    }
};

/**
 * Get archived pillars for a brand
 */
export const getArchivedPillarsByBrand = async (brandId) => {
    try {
        const q = query(
            collection(db, PILLARS_COLLECTION),
            where('brandId', '==', brandId),
            where('isArchived', '==', true)
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting archived pillars:', error);
        throw error;
    }
};

/**
 * Archive a pillar (soft delete)
 */
export const archivePillar = async (pillarId) => {
    try {
        return updatePillar(pillarId, { isArchived: true });
    } catch (error) {
        console.error('Error archiving pillar:', error);
        throw error;
    }
};

/**
 * Restore an archived pillar
 */
export const restorePillar = async (pillarId) => {
    try {
        return updatePillar(pillarId, { isArchived: false });
    } catch (error) {
        console.error('Error restoring pillar:', error);
        throw error;
    }
};

/**
 * Duplicate a pillar
 */
export const duplicatePillar = async (pillarId, userId, brandId, newName = null) => {
    try {
        const original = await getPillar(pillarId);
        if (!original) {
            throw new Error('Pillar not found');
        }

        const duplicate = {
            ...original,
            name: newName || `${original.name} (Copy)`,
            performance: DEFAULT_PILLAR.performance, // Reset performance
            frequency: DEFAULT_PILLAR.frequency // Reset frequency
        };
        delete duplicate.id;
        delete duplicate.createdAt;
        delete duplicate.updatedAt;

        return createPillar(duplicate, userId, brandId);
    } catch (error) {
        console.error('Error duplicating pillar:', error);
        throw error;
    }
};

/**
 * Create pillar from preset template
 */
export const createPillarFromPreset = async (presetName, userId, brandId) => {
    const preset = PILLAR_PRESETS.find(p => p.name === presetName);
    if (!preset) {
        throw new Error(`Preset "${presetName}" not found`);
    }
    return createPillar(preset, userId, brandId);
};

/**
 * Bulk create pillars from presets (for initial setup)
 */
export const bulkCreatePillarsFromPresets = async (presetNames, userId, brandId) => {
    const results = [];
    for (const presetName of presetNames) {
        const pillar = await createPillarFromPreset(presetName, userId, brandId);
        results.push(pillar);
    }
    return results;
};

/**
 * Update pillar performance metrics
 */
export const updatePillarPerformance = async (pillarId, metrics) => {
    try {
        const updates = {
            performance: {
                ...metrics,
                lastCalculated: serverTimestamp()
            }
        };
        return updatePillar(pillarId, updates);
    } catch (error) {
        console.error('Error updating pillar performance:', error);
        throw error;
    }
};

/**
 * Update pillar frequency settings
 */
export const updatePillarFrequency = async (pillarId, frequency) => {
    try {
        return updatePillar(pillarId, { frequency });
    } catch (error) {
        console.error('Error updating pillar frequency:', error);
        throw error;
    }
};

/**
 * Get pillars by funnel stage
 */
export const getPillarsByFunnelStage = async (brandId, stage) => {
    try {
        const pillars = await getPillarsByBrand(brandId);
        return pillars.filter(p => p.funnelMapping?.[stage]?.recommended);
    } catch (error) {
        console.error('Error getting pillars by funnel stage:', error);
        throw error;
    }
};

/**
 * Get pillars by awareness stage
 */
export const getPillarsByAwarenessStage = async (brandId, stage) => {
    try {
        const pillars = await getPillarsByBrand(brandId);
        return pillars.filter(p => p.awarenessMapping?.[stage]?.recommended);
    } catch (error) {
        console.error('Error getting pillars by awareness stage:', error);
        throw error;
    }
};

/**
 * Derive funnel mapping from awareness mapping
 * TOFU = unaware + problemAware
 * MOFU = solutionAware + productAware
 * BOFU = mostAware
 */
export const deriveFunnelMapping = (awarenessMapping) => {
    if (!awarenessMapping) return DEFAULT_PILLAR.funnelMapping;

    const tofu = {
        recommended: awarenessMapping.unaware?.recommended || awarenessMapping.problemAware?.recommended,
        weight: Math.max(
            awarenessMapping.unaware?.weight || 0,
            awarenessMapping.problemAware?.weight || 0
        )
    };

    const mofu = {
        recommended: awarenessMapping.solutionAware?.recommended || awarenessMapping.productAware?.recommended,
        weight: Math.max(
            awarenessMapping.solutionAware?.weight || 0,
            awarenessMapping.productAware?.weight || 0
        )
    };

    const bofu = {
        recommended: awarenessMapping.mostAware?.recommended,
        weight: awarenessMapping.mostAware?.weight || 0
    };

    return { TOFU: tofu, MOFU: mofu, BOFU: bofu };
};

/**
 * Calculate total frequency weight for a brand's pillars
 */
export const calculateTotalFrequencyWeight = async (brandId) => {
    try {
        const pillars = await getPillarsByBrand(brandId);
        return pillars.reduce((sum, p) => sum + (p.frequency?.currentWeight || 0), 0);
    } catch (error) {
        console.error('Error calculating total frequency weight:', error);
        throw error;
    }
};

/**
 * Normalize pillar frequency weights to 100%
 */
export const normalizePillarWeights = async (brandId) => {
    try {
        const pillars = await getPillarsByBrand(brandId);
        const total = pillars.reduce((sum, p) => sum + (p.frequency?.currentWeight || 0), 0);

        if (total === 0) return pillars;

        const updates = pillars.map(async (pillar) => {
            const normalizedWeight = Math.round((pillar.frequency?.currentWeight || 0) / total * 100);
            return updatePillarFrequency(pillar.id, {
                ...pillar.frequency,
                currentWeight: normalizedWeight
            });
        });

        return Promise.all(updates);
    } catch (error) {
        console.error('Error normalizing pillar weights:', error);
        throw error;
    }
};

/**
 * Redistribute weights evenly across active pillars.
 * Used after creating, archiving, or deleting a pillar.
 * Optionally exclude a pillar by ID (e.g. one being archived/deleted).
 */
export const redistributeWeightsEvenly = async (brandId, excludePillarId = null) => {
    try {
        const pillars = await getPillarsByBrand(brandId);
        const activePillars = excludePillarId
            ? pillars.filter(p => p.id !== excludePillarId)
            : pillars;

        if (activePillars.length === 0) return [];

        const baseWeight = Math.floor(100 / activePillars.length);
        const remainder = 100 - (baseWeight * activePillars.length);

        const updates = activePillars.map(async (pillar, index) => {
            const weight = baseWeight + (index < remainder ? 1 : 0);
            return updatePillarFrequency(pillar.id, {
                ...pillar.frequency,
                currentWeight: weight
            });
        });

        return Promise.all(updates);
    } catch (error) {
        console.error('Error redistributing pillar weights:', error);
        throw error;
    }
};

export default {
    createPillar,
    getPillar,
    getPillarsByBrand,
    getArchivedPillarsByBrand,
    updatePillar,
    savePillar,
    deletePillar,
    archivePillar,
    restorePillar,
    duplicatePillar,
    createPillarFromPreset,
    bulkCreatePillarsFromPresets,
    updatePillarPerformance,
    updatePillarFrequency,
    getPillarsByFunnelStage,
    getPillarsByAwarenessStage,
    deriveFunnelMapping,
    calculateTotalFrequencyWeight,
    normalizePillarWeights,
    redistributeWeightsEvenly,
    DEFAULT_PILLAR,
    AWARENESS_STAGES,
    FUNNEL_STAGES,
    PILLAR_PRESETS
};
